import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {motion} from 'framer-motion';
import Hero from '../common/Hero';
import EventCard from '../features/EventCard';
// Removed useFamilyData hook as we are fetching directly now
// Removed helper functions if the backend now handles sorting/filtering
import {isEventInPast} from '../../utils/helpers';
import DancingShield from "../common/DancingShield";

const EventsContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
`;

const TabsContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-bottom: 3rem;
`;

const Tab = styled.button`
    padding: 1rem 2rem;
    background: ${props => props.active ? '#6c5ce7' : 'transparent'};
    color: ${props => props.active ? 'white' : '#333'};
    border: 2px solid #6c5ce7;
    border-radius: ${props => props.position === 'left' ? '5px 0 0 5px' : props.position === 'right' ? '0 5px 5px 0' : '0'};
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;

    &:hover {
        background: ${props => props.active ? '#6c5ce7' : 'rgba(108, 92, 231, 0.1)'};
    }
`;

const EventsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 2rem;
`;

const NoEvents = styled.div`
    text-align: center;
    padding: 3rem;
    color: #666;
    font-size: 1.1rem;
    grid-column: 1 / -1;
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 3rem;
    color: #6c5ce7;
    font-size: 1.2rem;
    font-weight: 500;
    grid-column: 1 / -1;
`;

const ErrorMessage = styled.div`
    text-align: center;
    padding: 2rem;
    color: #e74c3c;
    background: #fadbd8;
    border-radius: 5px;
    margin-bottom: 2rem;
`;

const FilterContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    flex-wrap: wrap;
    gap: 1rem;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const FilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;

    label {
        font-weight: 500;
        color: #555;
    }

    select {
        padding: 0.5rem 1rem;
        border: 1px solid #ddd;
        border-radius: 5px;
        font-size: 1rem;

        &:focus {
            outline: none;
            border-color: #6c5ce7;
        }
    }
`;

const SearchInput = styled.input`
    padding: 0.5rem 1rem;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 1rem;
    min-width: 250px;

    &:focus {
        outline: none;
        border-color: #6c5ce7;
    }
`;

// New Styled Components for Pagination
const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
    margin-top: 3rem;
`;

const PageButton = styled.button`
    padding: 0.5rem 1rem;
    border: 1px solid #ddd;
    background: ${props => props.active ? '#6c5ce7' : 'white'};
    color: ${props => props.active ? 'white' : '#333'};
    border-radius: 5px;
    cursor: pointer;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background: ${props => props.active ? '#6c5ce7' : '#f0f0f0'};
        border-color: #6c5ce7;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const Events = () => {
    // State for Filter/Sort
    const [activeTab, setActiveTab] = useState('upcoming');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('desc');

    // State for Data & Pagination
    const [eventsData, setEventsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const ITEMS_PER_PAGE = 9;

    const API_URL = 'https://z184yo4b1e.execute-api.eu-west-1.amazonaws.com/prod/mlalazi/events';

    // Reset page to 1 if search, tab, or sort changes
    useEffect(() => {
        setCurrentPage(1);
    }, [activeTab, searchTerm, sortOrder]);

    // Fetch Events from API
    useEffect(() => {
        const fetchEvents = async () => {
            setLoading(true);
            setError(null);

            try {
                // Construct query parameters
                const params = new URLSearchParams({
                    page: currentPage,
                    limit: ITEMS_PER_PAGE,
                    type: activeTab, // 'upcoming' or 'past'
                    sort: sortOrder, // 'asc' or 'desc'
                    search: searchTerm
                });

                // Replace this URL with your actual API endpoint
                const response = await fetch(`${API_URL}?${params.toString()}`);

                if (!response.ok) {
                    throw new Error('Failed to fetch events');
                }

                const data = await response.json();
                // Assuming API returns format: { data: [], meta: { totalPages: 5 } }
                setEventsData(data.data || []);
                setTotalPages(data.meta?.totalPages || 1);

            } catch (err) {
                console.error("Error fetching events:", err);
                setError("Sorry, we could not fetch "+activeTab+" events for you. Check your network and load this page again...");
                // Optional: Fallback to empty array
                setEventsData([]);
            } finally {
                setLoading(false);
            }
        };

        // Debounce search to prevent excessive API calls
        const debounceId = setTimeout(() => {
            fetchEvents();
        }, 500);

        return () => clearTimeout(debounceId);

    }, [activeTab, searchTerm, sortOrder, currentPage]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            // Scroll to top of grid on page change
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    };

    return (
        <>
            <Hero
                title="Clan Events & News"
                subtitle="Creating memories together, one event at a time"
                backgroundImage="/assets/images/logo.jpg"
            />

            <EventsContainer>
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    transition={{duration: 0.5}}
                >
                    <TabsContainer>
                        <Tab
                            position="left"
                            active={activeTab === 'upcoming'}
                            onClick={() => setActiveTab('upcoming')}
                        >
                            Upcoming Events
                        </Tab>
                        <Tab
                            position="right"
                            active={activeTab === 'past'}
                            onClick={() => setActiveTab('past')}
                        >
                            Current & Past Articles
                        </Tab>
                    </TabsContainer>

                    <FilterContainer>
                        <SearchInput
                            type="text"
                            placeholder="Search events..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />

                        <FilterGroup>
                            <label htmlFor="sortOrder">Sort by Date:</label>
                            <select
                                id="sortOrder"
                                value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)}
                            >
                                <option value="asc">Earliest First</option>
                                <option value="desc">Latest First</option>
                            </select>
                        </FilterGroup>
                    </FilterContainer>

                    {error && <ErrorMessage>{error}</ErrorMessage>}

                    <EventsGrid>
                        {loading ? (
                            <LoadingMessage><DancingShield />Loading events...</LoadingMessage>
                        ) : eventsData.length > 0 ? (
                            eventsData.map(event => (
                                <EventCard
                                    key={event.id}
                                    event={event}
                                    isPast={activeTab === 'past' || isEventInPast(event.date)}
                                />
                            ))
                        ) : (
                            <NoEvents>
                                <DancingShield /> <p> {error || `Sorry, there are no ${activeTab} events/articles ${searchTerm ? ` that match your search` : ''}...`}</p>
                            </NoEvents>
                        )}
                    </EventsGrid>

                    {/* Pagination Controls */}
                    {!loading && eventsData.length > 0 && totalPages > 1 && (
                        <PaginationContainer>
                            <PageButton
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                &laquo; Prev
                            </PageButton>

                            {/* Simple pagination logic: Show current page and surrounding pages */}
                            {[...Array(totalPages)].map((_, index) => {
                                const pageNum = index + 1;
                                // Only show pages close to current page or first/last (simplified)
                                if (
                                    pageNum === 1 ||
                                    pageNum === totalPages ||
                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                ) {
                                    return (
                                        <PageButton
                                            key={pageNum}
                                            active={currentPage === pageNum}
                                            onClick={() => handlePageChange(pageNum)}
                                        >
                                            {pageNum}
                                        </PageButton>
                                    );
                                } else if (
                                    pageNum === currentPage - 2 ||
                                    pageNum === currentPage + 2
                                ) {
                                    return <span key={pageNum}>...</span>;
                                }
                                return null;
                            })}

                            <PageButton
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                Next &raquo;
                            </PageButton>
                        </PaginationContainer>
                    )}

                </motion.div>
            </EventsContainer>
        </>
    );
};

export default Events;